import * as DOMHelper from './DOMHelper';
import { MsalAuthenticationContextFactory } from './MsalAuthenticationContextFactory';
import { CascadingMsalAccountComponentInteropFactory } from "./CascadingMsalAccountComponentInteropFactory";
import { BlazorLoader } from "./BlazorLoader";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { BrowserConnectionServiceJavaScript } from "./BrowserConnectionService";
import { AuthenticatedFunctionsApi } from "./AuthenticatedFunctionsApi";
import { MarketplacesApp } from './MarketplacesApp';
import { render, Show } from "solid-js/web";
import { createSignal } from "solid-js";
import { AppControllerContext } from "./AppControllerContext";
import { SvgChartComponentInterop } from "./SvgChartComponentInterop";
import { Loading } from "./Loading";
const nonBlazorAppDomRootId = "non-blazor-app";
const blazorAppDomRootId = "app";
export class AppController {
    constructor(appSettings, appInsights, msalAuthenticationContext) {
        Object.defineProperty(this, "appSettings", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "appInsights", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "msalAuthenticationContext", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "BrowserConnectionServiceJavaScript", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "authenticatedFunctionsApi", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "latestBlazorLoader", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "getRequiresBlazor", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "setRequiresBlazor", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_userSession", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "onActiveMsalAccountChanged", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (newActiveAccount) => {
                const currentActiveAccount = this.userSession.getAccount();
                if (newActiveAccount) {
                    if (newActiveAccount?.idToken == currentActiveAccount?.idToken) {
                        console.log("onActiveMsalAccountChanged but activeAccount was the same. No action required. currentActiveAccount:", currentActiveAccount, "newActiveAccount", newActiveAccount);
                    }
                    else {
                        console.log("onActiveMsalAccountChanged, currentActiveAccount:", currentActiveAccount, "newActiveAccount", newActiveAccount);
                        this.userSession.setAccount(newActiveAccount);
                        if (newActiveAccount?.localAccountId) {
                            if (currentActiveAccount?.localAccountId === newActiveAccount.localAccountId) {
                            }
                            else {
                                console.log("The new active account has a different ID", newActiveAccount.localAccountId, "from the current active account", currentActiveAccount?.localAccountId, "so we must update the userSessionData");
                                const userSessionData = await this.authenticatedFunctionsApi.invokeQuery("Users/GetCurrentUser", { AggregateRootId: newActiveAccount.localAccountId });
                                const marketplacePermissions = userSessionData.AggregateRootWithPrivacyAuthorizationFilterResult.AggregateRootWithPrivacyFilterApplied?.R?.I ?? new Map();
                                const venuePermissions = userSessionData.AggregateRootWithPrivacyAuthorizationFilterResult.AggregateRootWithPrivacyFilterApplied?.E?.I ?? {};
                                const marketplacePermissionsKeys = Object.keys(marketplacePermissions);
                                const venuePermissionsKeys = Object.keys(venuePermissions);
                                this.userSession.setUserSessionData(userSessionData);
                                this.userSession.setUserMarketplacesIds(marketplacePermissionsKeys);
                                this.userSession.setUserVenuesIds(venuePermissionsKeys);
                                if (marketplacePermissionsKeys.length === 0) {
                                    if (venuePermissionsKeys.length === 0) {
                                        console.warn("The user has no marketplaces or venues");
                                    }
                                    else {
                                        console.log("The user doesn't have access to any marketplaces, only to Venues. Loading blazor now...");
                                        await this.showVenueSelector();
                                    }
                                }
                                else {
                                    if (venuePermissionsKeys.length == 0) {
                                        console.log("The user doesn't have access to any Venues, only Marketplaces. Loading MarketplacesApp now...");
                                        await this.startMarketplacesApp(marketplacePermissionsKeys, venuePermissionsKeys);
                                    }
                                    else {
                                        console.log(`The user has access to ${venuePermissionsKeys.length} Venues and ${marketplacePermissionsKeys.length} Marketplaces. Loading MarketplacesApp now...`);
                                        await this.startMarketplacesApp(marketplacePermissionsKeys, venuePermissionsKeys);
                                    }
                                }
                            }
                        }
                    }
                }
                else {
                    console.warn("The new active account is undefined!", newActiveAccount);
                }
            }
        });
        Object.defineProperty(this, "start", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                window.appInsights = this.appInsights;
                window.BrowserConnectionServiceJavaScript = this.BrowserConnectionServiceJavaScript;
                window.getUserAgentData = () => navigator.userAgentData;
                window.getBlazorLoadingDetails = () => this.latestBlazorLoader?.getLoadingDetails() ?? { resourcesToLoadManually: [] };
                window.getIsChromiumBrowser = AppController.getIsChromiumBrowser;
                window.showNotification = AppController.showNotification;
                window.muteAllVideosExceptOne = AppController.muteAllVideosExceptOne;
                window.muteAllVideos = AppController.muteAllVideos;
                window.buildSvgChartComponentInterop = AppController.buildSvgChartComponentInterop;
                window.logOut = this.logOut;
                await this.msalAuthenticationContext.initialize();
                const nonBlazorAppDomRoot = DOMHelper.getElementByIdOrFail(nonBlazorAppDomRootId);
                nonBlazorAppDomRoot.replaceChildren();
                render(() => <AppControllerContext.Provider value={this}>
                <Show when={!this.userSession.getUserSessionData()}>
                    <div style="height: 100vh; display: flex; justify-content: center; align-items: center;">
                        <Loading resourceName={"..."}/>
                    </div>
                </Show>
            </AppControllerContext.Provider>, nonBlazorAppDomRoot);
                const msalRedirectResult = await this.msalAuthenticationContext.handleRedirect();
                if (!msalRedirectResult) {
                    await this.msalAuthenticationContext.acquireTokenForActiveAccountSilentlyOrWithPopupIfInteractionRequired(this.appSettings.ApiTokenRequest.scopes);
                }
                if (document.readyState !== 'loading') {
                    console.debug("document.readyState", document.readyState);
                    this.initializeDom();
                }
                else {
                    console.debug("document.addEventListener DOMContentLoaded");
                    document.addEventListener("DOMContentLoaded", async () => {
                        console.debug("on document.DOMContentLoaded");
                        this.initializeDom();
                    });
                }
            }
        });
        Object.defineProperty(this, "startMarketplacesApp", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async (userMarketplacesIds, userVenueIds) => {
                render(() => <Show when={!this.getRequiresBlazor()}>
                <AppControllerContext.Provider value={this}>
                    <MarketplacesApp userMarketplacesIds={userMarketplacesIds} userVenueIds={userVenueIds}/>
                </AppControllerContext.Provider>
            </Show>, DOMHelper.getElementByIdOrFail(nonBlazorAppDomRootId));
            }
        });
        Object.defineProperty(this, "showVenueSelector", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => {
                this.setRequiresBlazor(true);
                const nonBlazorAppDomRoot = DOMHelper.getElementByIdOrFail(nonBlazorAppDomRootId);
                const blazorAppDomRoot = DOMHelper.getElementByIdOrFail(blazorAppDomRootId);
                const blazorLoader = BlazorLoader.createWithWindowBlazorRuntime(this.msalAuthenticationContext, blazorAppDomRoot, DOMHelper.getElementByIdOrFail("loading-files-details-container"), DOMHelper.getElementByIdOrFail("blazor-splash-container"), DOMHelper.getElementByIdOrFail("loading-error-container"), DOMHelper.getElementByIdOrFail("loading-progress-message-container"), this.appSettings.CurrentVersion);
                nonBlazorAppDomRoot.style.display = "none";
                this.latestBlazorLoader = blazorLoader;
                await blazorLoader.loadBlazor();
            }
        });
        Object.defineProperty(this, "initializeDom", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                console.debug("initializeDom");
                const popupLogInInteractionRequiredButton = DOMHelper.getElementByIdOrFail("popupLogInInteractionRequiredButton");
                popupLogInInteractionRequiredButton.addEventListener('click', async () => {
                    await this.msalAuthenticationContext.acquireTokenPopup(this.appSettings.ApiTokenRequest.scopes);
                });
                const popupLogOutInteractionRequiredButton = DOMHelper.getElementByIdOrFail("popupLogOutInteractionRequiredButton");
                popupLogOutInteractionRequiredButton.addEventListener('click', async () => {
                    await this.msalAuthenticationContext.logOutActiveAccount();
                });
            }
        });
        Object.defineProperty(this, "logOut", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: async () => await this.msalAuthenticationContext.logOutActiveAccount()
        });
        this.appSettings = appSettings;
        this.appInsights = appInsights;
        this.msalAuthenticationContext = msalAuthenticationContext;
        this.BrowserConnectionServiceJavaScript = new BrowserConnectionServiceJavaScript();
        this.authenticatedFunctionsApi = new AuthenticatedFunctionsApi(msalAuthenticationContext, appSettings.Queries.BaseUrl, appSettings.Queries.Scopes, appSettings.SystemCommandRequests.BaseUrl, appSettings.SystemCommandRequests.Scopes);
        const [getAccount, setAccount] = createSignal();
        const [getUserSessionData, setUserSessionData] = createSignal();
        const [getUserMarketplacesIds, setUserMarketplacesIds] = createSignal([]);
        const [getUserVenuesIds, setUserVenuesIds] = createSignal([]);
        this._userSession = {
            getAccount: getAccount,
            setAccount: setAccount,
            getUserSessionData: getUserSessionData,
            setUserSessionData: setUserSessionData,
            getUserMarketplacesIds: getUserMarketplacesIds,
            setUserMarketplacesIds: setUserMarketplacesIds,
            getUserVenuesIds: getUserVenuesIds,
            setUserVenuesIds: setUserVenuesIds
        };
        const [getRequiresBlazor, setRequiresBlazor] = createSignal(false);
        this.getRequiresBlazor = getRequiresBlazor;
        this.setRequiresBlazor = setRequiresBlazor;
    }
    get userSession() {
        return this._userSession;
    }
    set userSession(value) {
        this._userSession = value;
    }
    static async create() {
        const appSettingsResponse = await fetch("appsettings.json", {
            cache: "reload"
        });
        const appSettings = await appSettingsResponse.json();
        const msalConfig = appSettings.MsalConfig;
        const appInsights = AppController.initializeAppInsights();
        const msalAuthenticationContext = MsalAuthenticationContextFactory.createAuthenticationContext(msalConfig, AppController.showInteractiveLoginRequest, AppController.showInteractiveLogoutRequest);
        window.msalAuthenticationContextSingleton = msalAuthenticationContext;
        window.cascadingMsalAccountComponentInteropFactorySingleton = new CascadingMsalAccountComponentInteropFactory();
        const result = new AppController(appSettings, appInsights, msalAuthenticationContext);
        msalAuthenticationContext.setOnMsalActiveAccountChangedHandler(result);
        window.authenticatedFunctionsApiSingleton = result.authenticatedFunctionsApi;
        return result;
    }
    static initializeAppInsights() {
        if (window.appInsights) {
            console.warn("Tried to initializeAppInsights but it's already initialized");
            return window.appInsights;
        }
        else {
            const config = {
                src: "/ai.3.gbl.min.js",
                ld: -1,
                crossOrigin: "use-credentials",
                config: {
                    instrumentationKey: "00000000-0000-0000-0000-000000000000",
                    disableTelemetry: true
                }
            };
            const result = new ApplicationInsights(config);
            result.loadAppInsights();
            return result;
        }
    }
    static showInteractiveLoginRequest() {
        const popupLoginInteractionRequiredDialog = DOMHelper.getElementByIdOrFail("popupLoginInteractionRequiredDialog");
        popupLoginInteractionRequiredDialog.showModal();
    }
    static showInteractiveLogoutRequest() {
        const popupLogoutInteractionRequiredDialog = DOMHelper.getElementByIdOrFail("popupLogoutInteractionRequiredDialog");
        popupLogoutInteractionRequiredDialog.showModal();
    }
    static getIsChromiumBrowser() {
        return !!navigator.userAgentData && navigator.userAgentData.brands.some(data => data.brand == 'Chromium');
    }
    static requestNotificationPermission() {
        if (typeof Notification !== "undefined" && Notification.permission !== 'granted' && Notification.requestPermission) {
            return Notification.requestPermission();
        }
        else {
            return Promise.resolve(Notification.permission);
        }
    }
    static async showNotification(title, body, icon, requireInteraction) {
        await AppController.requestNotificationPermission();
        const options = {
            body,
            icon: icon ?? "logo.webp",
            requireInteraction: requireInteraction
        };
        new Notification(title, options);
    }
    static muteAllVideosExceptOne() {
        var allVideoElements = document.getElementsByTagName("video");
        let i = 0;
        for (let videoElement of allVideoElements) {
            if (i > 0) {
                videoElement.muted = true;
            }
            i++;
        }
    }
    static muteAllVideos() {
        var allVideoElements = document.getElementsByTagName("video");
        let i = 0;
        for (let videoElement of allVideoElements) {
            videoElement.muted = true;
            i++;
        }
    }
    static buildSvgChartComponentInterop(svgChartDomElementId, svgSelectionRectangleDomElementId) {
        const svgChartDomElement = DOMHelper.getElementByIdOrFail(svgChartDomElementId);
        const svgChartSelectionRectangleDomElement = DOMHelper.getElementByIdOrFail(svgSelectionRectangleDomElementId);
        const result = new SvgChartComponentInterop(svgChartDomElement, svgChartSelectionRectangleDomElement);
        return result;
    }
}
